import { useGetAccountQuery } from '@/services/accounts';
import { Box, Button, toast } from '@palmetto/palmetto-components';
import { Formik } from 'formik';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

import { AdderValues, AdderType } from 'types';

import { mapAddersValuesFromDocument } from '../../helpers/addersHelpers';
import { useGetAddersQuery, useUpdateAddersMutation } from '../../services/adders';
import { Sidebar } from '../Account/components/Sidebar';
import { SidebarSection } from '../Account/components/SidebarSection';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { EnhancedFormikForm } from '../EnhancedFormikForm';

import { AddersList } from './AddersList';
import { AddersSidebarContent } from './AddersSidebarContent';

export type AddersTabType = {
  selectedAdders: AdderType[];
};

export const adderItemSchema = yup.object({
  manufacturer: yup.string().nullable(),
  model: yup.string().nullable(),
  quantity: yup.number().nullable(),
});
export const adderArraySchema = yup.array().of(adderItemSchema).nullable();
export const addersSchema = yup.object({
  arbitrageBattery: adderArraySchema.optional().nullable(),
  backupBattery: adderArraySchema.optional().nullable(),
});

export const AddersTab = ({ selectedAdders }: AddersTabType) => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const accountId = id as string;
  const { data: addersData, isLoading: isAddersLoading } = useGetAddersQuery(accountId);
  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(accountId);
  const isLoading = isAddersLoading || isAccountLoading;
  const [saveAdders] = useUpdateAddersMutation();
  const initialValues = useMemo(
    () => mapAddersValuesFromDocument(addersData?.adders, selectedAdders),
    [addersData?.adders, selectedAdders],
  );
  const allowsAction = useIsOrgReadOnly();
  const selectedAddersMap = useMemo(
    () =>
      selectedAdders?.reduce(
        (acc, adder) => {
          acc[adder] = true;
          return acc;
        },
        {} as Record<AdderType, boolean>,
      ),
    [selectedAdders],
  );

  const handleSubmit = useCallback(
    async (values: AdderValues) => {
      try {
        await saveAdders({
          accountId,
          adders: { adders: Object.values(values).flat() },
        }).unwrap();
        toast.success('Adders Saved');
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error saving adders');
      }
    },
    [accountId, saveAdders],
  );

  return (
    <Box
      direction={{ base: 'column', desktop: 'row' }}
      borderColor="separator"
      borderWidth="xs"
      overflow="hidden"
      radius="md"
    >
      <Sidebar title="Adders">
        <SidebarSection>
          <AddersSidebarContent selectedAddersMap={selectedAddersMap} />
        </SidebarSection>
      </Sidebar>

      <Box width={{ base: '100', desktop: '70' }} childGap="lg" padding={{ base: 'lg', desktop: 'xl' }}>
        <Formik
          initialValues={initialValues}
          validationSchema={addersSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting }) => (
            <EnhancedFormikForm noValidate>
              {account && (
                <AddersList
                  selectedAdders={selectedAdders}
                  isLoading={isLoading}
                  programType={account.programType}
                  orgAlias={account.organizationId}
                />
              )}
              <Box
                direction={{
                  base: 'column',
                  tablet: 'row',
                }}
                alignItems={{
                  base: 'stretch',
                  tablet: 'flex-end',
                }}
                justifyContent={{
                  tablet: 'flex-end',
                }}
                childGap="sm"
                style={{ flexShrink: 0 }}
                padding="lg 0 0 0"
                borderColor="separator"
                borderWidth="xs 0 0 0"
              >
                <Button
                  as="a"
                  navigate={() => navigate(`/accounts/${accountId ?? ''}`)}
                  variant="secondary"
                  tone="neutral"
                  size="md"
                  isLoading={isSubmitting}
                >
                  Cancel
                </Button>
                <Button size="md" variant="primary" type="submit" isLoading={isSubmitting} disabled={!allowsAction}>
                  Save Adders
                </Button>
              </Box>
            </EnhancedFormikForm>
          )}
        </Formik>
      </Box>
    </Box>
  );
};
